import { useLocation } from 'react-router-dom';
import React from 'react';
import {
    SettingOutlined,
    HistoryOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    UserOutlined,
    FileTextOutlined,
    ProfileOutlined,
} from '@ant-design/icons';
import { MenuItem } from '@isocel/front-common/dist/components/MainMenu';
import { Dashboard, FlowOne, FlowThree, FlowTwo, Scanner, Transfer, User } from '@isocel/front-common';

import { Permission, RoleSlug } from '../queries/api/types';
import { getRoute, RoutePathName } from '../routes';
import LogoReceptionAttente from '../assets/icons/logoReceptionAttente';
import SvgLogoReceptionParcAttente from '../assets/icons/logoReceptionParcAttente';
import CounterSVG from '../assets/icons/counter';

// function to check if user should have access to "Mouvements divers"
export const canAccessMouvementsDivers = (user: User | null): boolean => {
    if (!user) return false;

    // Super admin always has access
    const isSuperAdmin = user.role?.slug === RoleSlug.superAdmin;
    if (isSuperAdmin) return true;

    // Organization admin and organization user always have access
    const isOrgAdmin = user.role?.slug === RoleSlug.orgAdmin;
    const isOrgUser = user.role?.slug === RoleSlug.orgUser;
    if (isOrgAdmin || isOrgUser) return true;

    // Store admin has access
    const isStoreAdmin = user.role?.slug === RoleSlug.storeAdmin;
    if (isStoreAdmin) return true;

    // Store user does NOT have access to Mouvements divers
    const isStoreUser = user.role?.slug === RoleSlug.storeUser;
    if (isStoreUser) {
        return false;
    }

    return false;
};

export const canAccessReceptionsAValider = (user: User | null): boolean => {
    if (!user) return false;
    const slug = user.role?.slug;
    return (
        slug === RoleSlug.storeAdmin ||
        slug === RoleSlug.storeUser ||
        slug === RoleSlug.orgAdmin ||
        slug === RoleSlug.orgUser
    );
};

export const menuItems = (receptionsCount = 0, litigesCount = 0, user: User | null = null): MenuItem[] => {
    const isOrgUser = user?.role?.slug === RoleSlug.orgAdmin || user?.role?.slug === RoleSlug.orgUser;
    const receptionsIcon = isOrgUser ? <SvgLogoReceptionParcAttente /> : <LogoReceptionAttente />;
    return [
        {
            key: getRoute(RoutePathName.dashboard),
            to: getRoute(RoutePathName.dashboard),
            label: 'Tableau de bord',
            icon: <Dashboard />,
            permission: Permission.dashboard,
        },
        {
            key: getRoute(RoutePathName.receptionsAValider),
            to: getRoute(RoutePathName.receptionsAValider),
            icon: <span style={{ marginLeft: isOrgUser ? '-14px' : '-4px' }}>{receptionsIcon}</span>,
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Réceptions à valider
                    {receptionsCount > 0 && (
                        <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{receptionsCount}</span>
                    )}
                </span>
            ),
        },
        ...(isOrgUser
            ? [
                  {
                      key: getRoute(RoutePathName.litigesAValider),
                      to: getRoute(RoutePathName.litigesAValider),
                      icon: <ExclamationCircleOutlined style={{ fontSize: 22 }} />,
                      label: (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              Litiges à valider
                              {litigesCount > 0 && (
                                  <span style={{ color: '#ff4d4f', fontWeight: 600 }}>{litigesCount}</span>
                              )}
                          </span>
                      ),
                  },
              ]
            : []),
        {
            key: getRoute(RoutePathName.afterLoading),
            to: getRoute(RoutePathName.afterLoading),
            label: 'Expédition vers magasin',
            icon: <FlowOne />,
            permission: Permission.orgUser,
        },
        {
            key: getRoute(RoutePathName.receptionStore),
            to: getRoute(RoutePathName.receptionStore),
            label: 'Réception en magasin',
            icon: <FlowTwo />,
            permission: Permission.storeUser,
        },
        {
            key: getRoute(RoutePathName.recipient),
            to: getRoute(RoutePathName.recipient),
            label: "Expédition retour d'emballages",
            icon: <FlowThree />,
            permission: Permission.storeUser,
        },
        {
            key: getRoute(RoutePathName.palettCreate),
            to: getRoute(RoutePathName.palettCreate),
            label: 'Mouvements divers',
            icon: <Transfer />,
        },
        {
            key: getRoute(RoutePathName.qrParcs),
            to: getRoute(RoutePathName.qrParcs),
            label: "Rendu d'emballages au transporteur",
            icon: <Scanner />,
            permission: Permission.orgUser,
        },
        {
            key: getRoute(RoutePathName.counters),
            to: getRoute(RoutePathName.counters),
            label: "Comptage d'emballages",
            icon: <CounterSVG />,
            permission: Permission.dashboard,
        },
        {
            key: getRoute(RoutePathName.inventoryHistory),
            to: getRoute(RoutePathName.inventoryHistory),
            label: 'Historique des inventaires',
            icon: (
                <span style={{ marginTop: '3px', marginLeft: '4px' }}>
                    <HistoryOutlined style={{ fontSize: 26 }} />
                </span>
            ),
            permission: Permission.dashboard,
        },
        {
            key: getRoute(RoutePathName.inventairesAValider),
            to: getRoute(RoutePathName.inventairesAValider),
            label: 'Inventaires à valider',
            icon: (
                <span style={{ marginTop: '3px', marginLeft: '4px' }}>
                    <CheckCircleOutlined style={{ fontSize: 26 }} />
                </span>
            ),
            permission: Permission.orgUser,
        },
        {
            key: getRoute(RoutePathName.gestionMagasins),
            to: getRoute(RoutePathName.gestionMagasins),
            label: 'Gestion des magasins',
            icon: (
                <span style={{ marginTop: '3px', marginLeft: '4px' }}>
                    <SettingOutlined style={{ fontSize: 26 }} />
                </span>
            ),
            permission: Permission.orgAdmin,
        },
        {
            key: 'gestion-utilisateurs',
            label: 'Gestion des utilisateurs',
            icon: <UserOutlined />,
            permission: Permission.orgAdmin,
            children: [
                {
                    key: getRoute(RoutePathName.scaUsers),
                    to: getRoute(RoutePathName.scaUsers),
                    label: 'Utilisateurs SCA',
                    icon: <UserOutlined />,
                    permission: Permission.orgAdmin,
                },
                {
                    key: getRoute(RoutePathName.storeUsers),
                    to: getRoute(RoutePathName.storeUsers),
                    label: 'Utilisateurs Magasin',
                    icon: <UserOutlined />,
                    permission: Permission.orgAdmin,
                },
            ],
        },
        {
            key: getRoute(RoutePathName.facturationManuelle),
            to: getRoute(RoutePathName.facturationManuelle),
            label: 'Facturation manuelle',
            icon: (
                <span style={{ marginTop: '3px', marginLeft: '4px' }}>
                    <FileTextOutlined style={{ fontSize: 26 }} />
                </span>
            ),
            permission: Permission.orgAdmin,
        },
        {
            key: getRoute(RoutePathName.historiqueFacturation),
            to: getRoute(RoutePathName.historiqueFacturation),
            label: 'Historique de facturation',
            icon: (
                <span style={{ marginTop: '3px', marginLeft: '4px' }}>
                    <ProfileOutlined style={{ fontSize: 26 }} />
                </span>
            ),
            permission: Permission.orgAdmin,
        },
    ];
};

export const getSelectedKey = (location: ReturnType<typeof useLocation>) => {
    const item = menuItems().find((item) => 'to' in item && item.to === location.pathname);
    return item ? item.key : '';
};
